import { useState, useCallback } from "react";
import { useToast } from "src/hooks/use-toast";
import { BookingSubmissionData } from "../types";
import { getTutorName } from "../utils/tutorHelpers";
import { emailService } from "src/services/emailService";
import { reminderScheduler } from "src/services/reminderScheduler";
import { format } from "date-fns";
import { Tutor } from "src/services/tutorsService";
import { teamsService } from "src/services/teamsServiceBackend";

export const useBookingSubmission = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const submitBooking = useCallback(async (bookingData: BookingSubmissionData, tutor?: Tutor): Promise<boolean> => {
    setIsSubmitting(true);

    try {
      // 1. Submit the booking to the backend
      console.log('💾 Saving booking to database...');
      // Simulate API call with a delay (replace with actual API call)
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Generate a session ID (in production, this would come from the API)
      const sessionId = `demo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // 2. Try to create Teams meeting, but don't fail booking if it fails
      console.log('🎥 Creating Teams meeting...');
      const startTime = new Date(bookingData.date);
      const endTime = new Date(startTime.getTime() + bookingData.duration * 60000);
      const teamsSessionData = {
        subject: bookingData.topic,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        organizerId: tutor?.id, // Assuming tutor.id is the organizerId
        participants: [bookingData.userInfo.email, tutor?.email || ''],
        description: `Demo session: ${bookingData.topic}`,
      };

      let meetingUrl: string | undefined;
      try {
        const meetingCreated = await teamsService.createAndStoreMeeting(sessionId, teamsSessionData);
        if (meetingCreated) {
          // Fetch the meeting URL (this step might vary based on your implementation)
          const meetingDetails = await teamsService.getMeetingDetails(sessionId);
          meetingUrl = meetingDetails.joinUrl;
          console.log('✅ Teams meeting created successfully');
          console.log('🔗 Meeting URL:', meetingUrl);
        } else {
          throw new Error('Failed to create Teams meeting');
        }
      } catch (teamsError) {
        console.warn('⚠️ Teams meeting creation failed, proceeding with fallback email:', teamsError);
        meetingUrl = undefined; // Explicitly unset, we will communicate fallback in email
      }

      // 3. Send confirmation email to the student/parent (always attempt)
      try {
        console.log('📧 Preparing to send demo booking confirmation email...');

        // Format the data for the email
        const studentName = bookingData.userInfo.studentName;
        const tutorName = tutor ? getTutorName(tutor) : 'Your Tutor';
        const formattedDate = format(bookingData.date, 'EEEE, MMMM d, yyyy');
        const formattedTime = bookingData.timeSlot;
        const timezone = bookingData.timezone || 'UTC';

        const emailResult = await emailService.sendDemoBookingConfirmation(
          bookingData.userInfo.email,
          {
            studentName: studentName,
            tutorName: tutorName,
            tutorEmail: tutor?.email || '',
            date: formattedDate,
            time: formattedTime,
            duration: `${bookingData.duration} minutes`,
            subject: bookingData.topic,
            meetingUrl: meetingUrl,
            timezone: timezone
          }
        );

        if (emailResult.success) {
          console.log('✅ Demo booking confirmation email sent successfully');
          console.log('📧 Email ID:', emailResult.emailId);
        } else {
          console.error('❌ Failed to send confirmation email:', emailResult.error);
          console.error('📧 Email error details:', emailResult.details);
        }
      } catch (emailError) {
        console.error('❌ Email service error:', emailError);
      }

      // 4. Try to schedule session reminders only if we have a meeting
      try {
        if (meetingUrl) {
          console.log('⏰ Scheduling session reminders...');
          const reminderSuccess = await reminderScheduler.scheduleForSession(sessionId);
          if (reminderSuccess) {
            console.log('✅ Session reminders scheduled successfully');
          } else {
            console.warn('⚠️ Failed to schedule session reminders, but booking is still successful');
          }
        } else {
          console.log('ℹ️ Skipping reminder scheduling because meeting URL is not available yet');
        }
      } catch (reminderError) {
        console.error('❌ Error scheduling session reminders:', reminderError);
        // Don't fail the booking if reminder scheduling fails
      }

      // 5. Show success message (reflect whether meeting link is ready)
      const successMessage = `Your demo session with ${tutor ? getTutorName(tutor) : 'your tutor'} has been recorded for ${format(bookingData.date, 'MMMM d, yyyy')} at ${bookingData.timeSlot}.`;
      const linkNote = meetingUrl
        ? `A confirmation email with the Teams meeting link was sent to ${bookingData.userInfo.email}.`
        : `A confirmation email was sent to ${bookingData.userInfo.email}. We will share the meeting invite soon.`;
      
      toast({
        title: meetingUrl ? "Demo session booked successfully! 🎉" : "Demo request recorded!",
        description: `${successMessage} ${linkNote}`,
      });

      console.log('✅ Booking submission completed successfully');
      return true;

    } catch (error) {
      console.error('❌ Booking submission error:', error);
      
      toast({
        title: "Booking failed",
        description: "There was an error booking your demo session. Please try again or contact support.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [toast]);

  return {
    submitBooking,
    isSubmitting,
  };
};
